function PrivacyPolicy() {
  return (
    <main className="page simple-page">
      <div className="simple-inner policy">
        <h1>Privacy Policy</h1>
        <p className="policy-meta">Effective Date: August 27, 2026</p>

        <h2>1. Introduction</h2>
        <p>
          Within: Mindful App Blocker ("Within", "we", "our", or "us") respects your
          privacy.
        </p>
        <p>
          Within is designed so that its core functionality, including app blocking
          preferences and Screen Time-related features, operates primarily on your
          device.
        </p>
        <p>
          We use limited third-party services for analytics and subscription management.
          Specifically, we use Firebase Analytics to understand how the app is used and
          RevenueCat to manage subscriptions and in-app purchases.
        </p>
        <p>
          This Privacy Policy explains what information may be collected, how it is used,
          and how it is handled when you use Within.
        </p>

        <h2>2. Information We Collect</h2>
        <h3>a) Personal Information</h3>
        <p>Within does not require you to create an account.</p>
        <p>
          We do not directly ask for or collect your name, phone number, address,
          contacts, or similar personal information through the app.
        </p>
        <p>
          If you voluntarily contact us for support, we may receive your email address
          and any information included in your message. We use this information only to
          respond to your inquiry and provide support.
        </p>

        <h3>b) Analytics Data</h3>
        <p>
          Within uses Firebase Analytics, a service provided by Google LLC, to help us
          understand how the app is used and improve its functionality.
        </p>
        <p>Firebase Analytics may collect information such as:</p>
        <ul className="policy-list">
          <li>App interactions and usage events</li>
          <li>Features used within the app</li>
          <li>App version</li>
          <li>Device type and model</li>
          <li>Operating system and version</li>
          <li>Language and general region</li>
          <li>App instance identifiers</li>
          <li>General technical and diagnostic information</li>
        </ul>
        <p>
          We may use this information to understand usage patterns, evaluate features,
          identify technical issues, and improve Within.
        </p>
        <p>
          We do not use Firebase Analytics to access the content of your messages,
          photos, contacts, personal files, or other applications.
        </p>
        <p>We do not use analytics data for personalized advertising.</p>

        <h3>c) Subscription and Purchase Information</h3>
        <p>
          Within uses RevenueCat, a third-party subscription management service provided
          by RevenueCat, Inc., to manage in-app purchases and subscriptions.
        </p>
        <p>
          When you purchase, renew, or restore a subscription, RevenueCat may process
          information necessary to verify and manage your subscription, including:
        </p>
        <ul className="policy-list">
          <li>Purchase and subscription history</li>
          <li>Purchase receipts</li>
          <li>Subscription status</li>
          <li>Renewal and expiration information</li>
          <li>Product identifiers</li>
          <li>App version</li>
          <li>Device and operating system information</li>
          <li>An app-specific user identifier</li>
        </ul>
        <p>
          If you do not create an account or otherwise identify yourself within Within,
          RevenueCat may use an automatically generated app-specific identifier to
          associate purchases with your installation.
        </p>
        <p>
          We use this information to provide premium features, verify subscription
          status, and restore purchases.
        </p>
        <p>
          Within does not receive or store your payment card or banking information.
          Payments are processed by Apple through the App Store.
        </p>
        <p>
          We do not use purchase or subscription information for personalized
          advertising.
        </p>

        <h3>d) Data Stored on Your Device</h3>
        <p>
          Within stores certain information locally on your device in order to provide
          its core functionality, including:
        </p>
        <ul className="policy-list">
          <li>App blocking preferences</li>
          <li>Selected unlock durations</li>
          <li>Unlock session history</li>
          <li>App settings and preferences</li>
        </ul>
        <p>
          This information is used by Within to operate the features you choose to use.
        </p>
        <p>
          The specific applications you choose to block and the Screen Time information
          required to enforce those restrictions are processed primarily on your device
          using Apple's system frameworks.
        </p>
        <p>We do not access the content of the applications you block.</p>

        <h2>3. Screen Time and System Permissions</h2>
        <p>
          Within may request access to Apple's Screen Time and related system frameworks
          in order to:
        </p>
        <ul className="policy-list">
          <li>Allow you to select applications to restrict</li>
          <li>Block selected applications</li>
          <li>Manage temporary unlock durations</li>
          <li>Apply restrictions selected by you</li>
        </ul>
        <p>
          Screen Time-related information required for these features is handled through
          Apple's system frameworks.
        </p>
        <p>
          Within does not access the contents of your applications, messages, photos,
          contacts, emails, or personal files through Screen Time permissions.
        </p>
        <p>We do not use Screen Time information for advertising purposes.</p>

        <h2>4. Third-Party Services</h2>
        <p>Within uses the following third-party services:</p>
        <h3>Firebase Analytics</h3>
        <p>
          Firebase Analytics, provided by Google LLC, is used to understand general app
          usage and improve Within.
        </p>
        <p>
          Firebase may process limited analytics, device, identifier, and technical
          information on our behalf.
        </p>
        <h3>RevenueCat</h3>
        <p>
          RevenueCat, provided by RevenueCat, Inc., is used to manage subscriptions,
          verify purchases, determine subscription status, and restore purchases.
        </p>
        <p>
          RevenueCat may process purchase, subscription, app, device, and app-specific
          identifier information as necessary to provide these services.
        </p>
        <p>
          These third-party providers process information according to their own privacy
          policies and applicable data protection terms.
        </p>

        <h2>5. Data Sharing</h2>
        <p>We do not sell or rent your personal information.</p>
        <p>
          We do not share information with third parties for their own personalized
          advertising or marketing purposes.
        </p>
        <p>
          Information may be processed by service providers such as Google Firebase and
          RevenueCat as necessary to provide analytics and subscription functionality.
        </p>
        <p>
          We may also disclose information if required by applicable law, regulation,
          legal process, or governmental request, or where reasonably necessary to
          protect the rights, security, or integrity of Within and its users.
        </p>

        <h2>6. Data Retention</h2>
        <p>
          Information stored locally by Within generally remains on your device until it
          is removed by the app, reset by you, or deleted when the app is uninstalled.
        </p>
        <p>
          Analytics information collected through Firebase Analytics may be retained
          according to our Firebase configuration and Google's applicable data retention
          practices.
        </p>
        <p>
          Subscription and purchase information processed through RevenueCat may be
          retained as necessary to manage subscriptions, maintain purchase records,
          restore purchases, and comply with applicable legal obligations.
        </p>
        <p>
          Deleting Within from your device may remove locally stored app information, but
          it does not necessarily immediately delete information that was previously
          transmitted to Firebase Analytics, RevenueCat, or Apple.
        </p>

        <h2>7. Your Choices</h2>
        <p>You may stop further collection of information by uninstalling Within.</p>
        <p>
          You may manage or cancel your subscription through your Apple App Store account
          settings.
        </p>
        <p>
          Where applicable under privacy laws, you may also have rights relating to your
          personal data, including rights to request access, correction, deletion,
          restriction, or other information about how your data is processed.
        </p>
        <p>
          You may contact us using the email address below if you have a privacy-related
          request.
        </p>

        <h2>8. Children's Privacy</h2>
        <p>Within is not specifically directed to children.</p>
        <p>
          We do not knowingly collect personal information from children through account
          registration or similar mechanisms.
        </p>
        <p>
          If we become aware that personal information from a child has been collected in
          a manner that requires parental consent under applicable law, we will take
          appropriate steps to delete or otherwise handle that information as required.
        </p>

        <h2>9. Security</h2>
        <p>
          We take reasonable measures to protect information handled through Within and
          the third-party services used by the app.
        </p>
        <p>
          However, no method of electronic transmission or storage can be guaranteed to
          be completely secure.
        </p>

        <h2>10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy if Within's functionality, third-party
          services, data practices, or applicable legal requirements change.
        </p>
        <p>
          When we make changes, we will update the "Effective Date" at the top of this
          Privacy Policy.
        </p>

        <h2>11. Contact</h2>
        <p>
          If you have questions or requests regarding this Privacy Policy, you may
          contact us at:
        </p>
        <p>
          <a href="mailto:within-mindful-app@outlook.com">
            within-mindful-app@outlook.com
          </a>
        </p>
      </div>
    </main>
  );
}

export default PrivacyPolicy;
